import { useMemo } from "react";
import { useTranslation } from "@repo/core";
import type { IndicatorTimeline } from "../../../types/factor-mimicking";

// Import ApexCharts components
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

interface PerformanceChartProps {
  timelines: IndicatorTimeline[];
  isLoading?: boolean;
  className?: string;
  hasSelection?: boolean;
}

/**
 * Performance chart component for displaying indicator timelines
 */
export const PerformanceChart = ({
  timelines,
  isLoading = false,
  hasSelection = false,
}: PerformanceChartProps) => {
  const { tMimicking, i18n } = useTranslation();

  // Convert timeline data to ApexCharts format
  const chartSeries = timelines.map((timeline) => ({
    name: timeline.indicatorName,
    data: timeline.data.map((point) => [
      new Date(point.date).getTime(),
      point.value,
    ]),
  }));

  const chartOptions: ApexOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: 400,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        defaultLocale: i18n.language === 'cs' ? 'cs' : 'en',
        locales: [
          {
            name: 'en',
            options: {
              months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
              shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
              days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
              shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
              toolbar: {
                download: 'Download SVG',
                selection: 'Selection',
                selectionZoom: 'Selection Zoom',
                zoomIn: 'Zoom In',
                zoomOut: 'Zoom Out',
                pan: 'Panning',
                reset: 'Reset Zoom'
              }
            }
          },
          {
            name: 'cs',
            options: {
              months: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'],
              shortMonths: ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'],
              days: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
              shortDays: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],
              toolbar: {
                download: 'Stáhnout SVG',
                selection: 'Výběr',
                selectionZoom: 'Výběr pro zoom',
                zoomIn: 'Přiblížit',
                zoomOut: 'Oddálit',
                pan: 'Posun',
                reset: 'Reset zoomu'
              }
            }
          }
        ]
      },
      stroke: {
        width: 2,
        curve: "smooth",
      },
      xaxis: {
        type: "datetime",
        title: {
          text: tMimicking("performanceChart.xAxis"),
        },
      },
      yaxis: {
        title: {
          text: tMimicking("performanceChart.yAxis"),
        },
        labels: {
          formatter: (value) => `${(value - 100).toFixed(0)}$`,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          format: "yyyy",
        },
        y: {
          formatter: (value, { seriesIndex, w }) => {
            const seriesName = w.globals.seriesNames[seriesIndex];
            return `${seriesName}: ${value.toFixed(1)}$`;
          },
        },
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const date = new Date(w.globals.seriesX[seriesIndex][dataPointIndex]);
          const currentLocale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
          const formattedDate = date.toLocaleDateString(currentLocale, {
            year: 'numeric',
            month: 'short'
          });

          let tooltipContent = `
            <div class="bg-white border border-gray-200 rounded shadow-lg overflow-hidden">
              <div class="bg-gray-100 px-3 py-2 border-b border-gray-200">
                <div class="font-semibold text-gray-900 text-sm">${formattedDate}</div>
              </div>
              <div class="p-3">
          `;

          // Add all series values for this data point
          for (let i = 0; i < series.length; i++) {
            const seriesName = w.globals.seriesNames[i];
            const value = series[i][dataPointIndex];
            if (value !== null && value !== undefined) {
              const color = w.globals.colors[i];
              tooltipContent += `
                <div class="flex items-center justify-between gap-3 py-1">
                  <div class="flex items-center gap-2">
                    <div class="w-3 h-3 rounded-full" style="background-color: ${color}"></div>
                    <span class="text-sm text-gray-700">${seriesName}:</span>
                  </div>
                  <span class="text-sm font-medium text-gray-900">${value.toFixed(
                1
              )}$</span>
                </div>
              `;
            }
          }

          tooltipContent += `</div></div>`;
          return tooltipContent;
        },
      },
      legend: {
        show: timelines.length <= 5, // Only show legend if not too many lines
        position: "top",
        horizontalAlign: "left",
      },
      grid: {
        show: true,
        strokeDashArray: 3,
      },
      colors: [
        "#3B82F6", // Blue
        "#10B981", // Green
        "#8B5CF6", // Purple
        "#F59E0B", // Orange
        "#EF4444", // Red
        "#06B6D4", // Cyan
        "#84CC16", // Lime
        "#F97316", // Orange
        "#EC4899", // Pink
        "#6B7280", // Gray
      ],
    };
  }, [timelines.length, i18n.language, tMimicking]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="h-6 bg-gray-200 rounded w-48 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  if (timelines.length === 0) {
    return (
      <div className="p-6">
        <div className="mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {tMimicking("performanceChart.title")}
            </h3>
            <p className="text-gray-600 text-sm">
              {tMimicking("performanceChart.subtitle")}
            </p>
          </div>
        </div>

        <div className="h-96 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {hasSelection
                ? tMimicking("performanceChart.noData.title")
                : tMimicking("performanceChart.noSelection.title")}
            </h3>
            <p className="text-gray-600">
              {hasSelection
                ? tMimicking("performanceChart.noData.description")
                : tMimicking("performanceChart.noSelection.description")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-tour="performance-chart">
      {/* Chart Header */}
      <div className="mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {tMimicking("performanceChart.title")}
          </h3>
          <p className="text-gray-600 text-sm">
            {tMimicking("performanceChart.subtitle")}
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="h-96">
        <ReactApexChart
          options={chartOptions}
          series={chartSeries}
          type="line"
          height="100%"
        />
      </div>

      {/* Chart Legend (if too many lines to show built-in legend) */}
      {timelines.length > 5 && (
        <div className="mt-4 flex flex-wrap gap-4">
          {timelines.map((timeline, index) => (
            <div key={timeline.indicatorId} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor:
                    chartOptions.colors?.[index % chartOptions.colors.length],
                }}
              />
              <span className="text-sm text-gray-700 truncate max-w-32">
                {timeline.indicatorName}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PerformanceChart;
